
import React from 'react';
import { Bot, Star, Shield } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ExchangePlatform } from '@/types/trader';
import { formatExchanges } from './utils/formatting';
import { iconColorMap } from './utils/styles';
import { useIsMobile } from '@/hooks/use-mobile';

interface CardHeaderProps {
  name: string;
  risk: string;
  riskColor: string;
  description: string;
  tradingStyle: string;
  isFeatured: boolean;
  isNew: boolean;
  isBestSeller: boolean;
  exchanges: ExchangePlatform[] | null;
  colorScheme?: string;
}

export const PremiumBotCardHeader = ({
  name,
  risk,
  riskColor,
  description,
  tradingStyle,
  isFeatured,
  isNew,
  isBestSeller,
  exchanges,
  colorScheme,
}: CardHeaderProps) => {
  const iconStyle = colorScheme ? iconColorMap[colorScheme] : '';
  const isMobile = useIsMobile();
  
  return (
    <CardHeader className={`pb-2 ${isMobile ? "p-4" : "p-6"}`}>
      <div className="flex justify-between items-start">
        <div className="flex flex-col">
          <div className="flex items-center gap-1.5 md:gap-2">
            <Bot className={cn(`${isMobile ? "h-4 w-4" : "h-5 w-5"}`, iconStyle)} />
            <CardTitle className={`font-medium ${isMobile ? "text-base" : "text-lg"}`}>{name}</CardTitle>
          </div>
          
          <div className="flex flex-wrap gap-1 mt-1">
            <Badge variant="outline" className={`${riskColor} ${isMobile ? "text-[10px] py-0" : "text-xs"}`}>
              <Shield className={`${isMobile ? "mr-0.5 h-2.5 w-2.5" : "mr-1 h-3 w-3"}`} />
              {risk}
            </Badge>
            
            <Badge variant="outline" className={`bg-blue-500/10 text-blue-500 border-blue-500/20 ${isMobile ? "text-[10px] py-0" : "text-xs"}`}>
              {tradingStyle}
            </Badge>
          </div>
        </div>
        
        <div className="flex flex-col items-end gap-1">
          {isFeatured && (
            <Badge variant="outline" className={`bg-purple-500/10 text-purple-500 border-purple-500/20 ${isMobile ? "text-[10px] py-0" : "text-xs"}`}>
              <Star className={`${isMobile ? "mr-0.5 h-2.5 w-2.5" : "mr-1 h-3 w-3"} fill-purple-500`} /> Featured
            </Badge>
          )}
          
          {isNew && (
            <Badge variant="outline" className={`bg-green-500/10 text-green-500 border-green-500/20 ${isMobile ? "text-[10px] py-0" : "text-xs"}`}>
              New
            </Badge>
          )}
          
          {isBestSeller && (
            <div className="absolute top-0 right-0">
              <img 
                src="/lovable-uploads/55881d4a-e292-4b84-bfe3-23f26d5b086a.png" 
                alt="Best Seller" 
                className={`${isMobile ? "h-10" : "h-12"} w-auto`}
              />
            </div>
          )}
        </div>
      </div>
      
      {exchanges && exchanges.length > 0 && (
        <div className={`${isMobile ? "text-[10px]" : "text-xs"} text-muted-foreground mt-1`}>
          {formatExchanges(exchanges)}
        </div>
      )}
      
      <CardDescription className={`line-clamp-2 ${isMobile ? "h-8 mt-1.5 text-xs" : "h-10 mt-2 text-sm"}`}>
        {description}
      </CardDescription>
    </CardHeader>
  );
};
